/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/catalog.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * Closed. Every value has declared capabilities, because a connector referenced with no declared
 * capabilities forces the planner to guess what it can push down — which is how `if dialect ==
 * ORACLE` gets written into business logic.
 */
export type ConnectorType = 'postgresql' | 'oracle' | 'sqlserver' | 'mysql' | 'snowflake' | 'databricks' | 'bigquery';

export const CONNECTOR_TYPES: readonly ConnectorType[] = ['postgresql', 'oracle', 'sqlserver', 'mysql', 'snowflake', 'databricks', 'bigquery'];

export function isConnectorType(value: unknown): value is ConnectorType {
  return (['postgresql', 'oracle', 'sqlserver', 'mysql', 'snowflake', 'databricks', 'bigquery'] as readonly unknown[]).includes(value);
}

export type PiiKind = 'none' | 'pan' | 'aadhaar' | 'gstin' | 'ifsc' | 'card_number' | 'email' | 'phone' | 'name' | 'address' | 'other';

export const PII_KINDS: readonly PiiKind[] = ['none', 'pan', 'aadhaar', 'gstin', 'ifsc', 'card_number', 'email', 'phone', 'name', 'address', 'other'];

export function isPiiKind(value: unknown): value is PiiKind {
  return (['none', 'pan', 'aadhaar', 'gstin', 'ifsc', 'card_number', 'email', 'phone', 'name', 'address', 'other'] as readonly unknown[]).includes(value);
}

/**
 * * `pending` — not yet evaluated. * `proposed` — a classifier produced a verdict. **Not
 * authoritative.** * `approved` — a human accepted it. The only authoritative state. * `stale` —
 * was approved or proposed, and the classification checksum has since changed. Distinct from
 * `pending` because there is a prior decision, and who made it, worth showing the reviewer. *
 * `inconclusive` — the classifier failed or abstained. PII status **unknown**. Treat
 * restrictively; this is not "no PII found". Named rather than inline so a client can hold a value
 * of this type instead of a bare string. A misspelt comparison against a bare string compiles;
 * against a union of literals it does not, and the comparison this protects decides whether an
 * unknown PII verdict is rendered as though a human had approved it.
 */
export type ClassificationState = 'pending' | 'proposed' | 'approved' | 'stale' | 'inconclusive';

export const CLASSIFICATION_STATES: readonly ClassificationState[] = ['pending', 'proposed', 'approved', 'stale', 'inconclusive'];

export function isClassificationState(value: unknown): value is ClassificationState {
  return (['pending', 'proposed', 'approved', 'stale', 'inconclusive'] as readonly unknown[]).includes(value);
}

/**
 * `partial` is distinct from both neighbours on purpose. Reported as `succeeded` it would license
 * marking unobserved datasets vanished; reported as `failed` it would discard metadata that was
 * genuinely observed.
 */
export type HarvestState = 'pending' | 'running' | 'succeeded' | 'partial' | 'failed' | 'cancelled';

export const HARVEST_STATES: readonly HarvestState[] = ['pending', 'running', 'succeeded', 'partial', 'failed', 'cancelled'];

export function isHarvestState(value: unknown): value is HarvestState {
  return (['pending', 'running', 'succeeded', 'partial', 'failed', 'cancelled'] as readonly unknown[]).includes(value);
}

export interface ConnectorCapability {
  /**
   * Whether the source exposes a stable per-object identifier. `false` has a real cost and it is
   * surfaced rather than hidden: without an object id a rename is indistinguishable from a drop
   * plus a create, so schema history stays with the old identity instead of following the new
   * name. A UI registering a MySQL connection should say so up front rather than let an operator
   * find out afterwards.
   */
  readonly supplies_object_id: boolean;
  readonly supports_column_comments: boolean;
  readonly max_identifier_length: number;
}

export function isConnectorCapability(value: unknown): value is ConnectorCapability {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['supplies_object_id'] === 'boolean' &&
    typeof candidate['supports_column_comments'] === 'boolean' &&
    typeof candidate['max_identifier_length'] === 'number'
  );
}

export interface Connection {
  readonly id: string;
  readonly name: string;
  readonly connector_type: ConnectorType;
  readonly host: string;
  readonly port: number;
  readonly database: string;
  readonly options?: Readonly<Record<string, string>>;
  /**
   * `registered` means nobody has tried to reach it yet, which is different from `failed` —
   * collapsing the two makes a never-verified connection look broken. `disabled` means a person
   * took the source out of service. It is a **decision, not a verdict**, and it outranks one:
   * verifying a disabled source is allowed and does not move this field, because while a source is
   * out of service there is nothing for a reachability verdict to inform. Every automated path —
   * harvest, rule execution, profiling and the two monitor sweeps — refuses a disabled source by
   * name. `POST .../disable` and `POST .../enable` are the only writers; re-enabling returns it to
   * `registered`, since nothing has been checked since it came back.
   */
  readonly state: 'registered' | 'verified' | 'failed' | 'disabled';
  /**
   * Echoed back because it is a path, not a secret. Resolving it happens only inside a connector
   * adapter at connect time, and the resolved value never travels upward.
   */
  readonly secret_ref: string;
  /**
   * When a verification of this source was last **attempted**, successful or not. `null` means
   * never — which is true of every connection registered before this field existed as well as
   * every one nobody has tested, and a client must render it as "never" rather than as a zero age.
   * It dates the *attempt* rather than the verdict, so a source that was reached and refused 41
   * days ago reports that instant. `state` carries what was found; this carries how old that
   * finding is, and a verdict with no age is a claim whose staleness is unknowable. **Advanced
   * only by verification.** Renaming a source, taking it out of service or putting it back leaves
   * it alone, so a `registered` state beside a recent timestamp is a real and meaningful pair: it
   * means the source was checked, and then something happened that invalidated the verdict without
   * checking anything.
   */
  readonly last_checked_at: string | null;
  readonly capability: ConnectorCapability;
}

export function isConnection(value: unknown): value is Connection {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['name'] === 'string' &&
    (['postgresql', 'oracle', 'sqlserver', 'mysql', 'snowflake', 'databricks', 'bigquery'] as readonly unknown[]).includes(candidate['connector_type']) &&
    typeof candidate['host'] === 'string' &&
    typeof candidate['port'] === 'number' &&
    typeof candidate['database'] === 'string' &&
    (['registered', 'verified', 'failed', 'disabled'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['secret_ref'] === 'string' &&
    (typeof candidate['last_checked_at'] === 'string' || candidate['last_checked_at'] === null) &&
    candidate['capability'] !== undefined
  );
}

export interface VerificationResult {
  readonly reachable: boolean;
  /**
   * Null when reachable. Otherwise the endpoint and the failure's type — never the driver's
   * message, which in several drivers contains the credential.
   */
  readonly detail: string | null;
  readonly connection: Connection;
}

export function isVerificationResult(value: unknown): value is VerificationResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['reachable'] === 'boolean' &&
    (typeof candidate['detail'] === 'string' || candidate['detail'] === null) &&
    candidate['connection'] !== undefined
  );
}

export interface ColumnDefinition {
  readonly ordinal: number;
  readonly name: string;
  readonly data_type: string;
  readonly nullable: boolean;
  readonly comment?: string | null;
}

export function isColumnDefinition(value: unknown): value is ColumnDefinition {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['ordinal'] === 'number' &&
    typeof candidate['name'] === 'string' &&
    typeof candidate['data_type'] === 'string' &&
    typeof candidate['nullable'] === 'boolean'
  );
}

export interface Dataset {
  /**
   * Stable for the life of the dataset. Survives renames and every schema change. **Key
   * client-side state on this**, not on `schema_name.object_name`.
   */
  readonly id: string;
  readonly connection_id: string;
  readonly schema_name: string;
  /** An attribute, not an identity. It changes when the source object is renamed. */
  readonly object_name: string;
  /**
   * The source's own identifier, where it has one. Null means renames on this source cannot be
   * tracked — see `ConnectorCapability.supplies_object_id`.
   */
  readonly source_object_id?: string | null;
  /**
   * `vanished` means a *fully successful* harvest observed the object to be gone. Never set from a
   * partial harvest, whose silence about an object is an absence of evidence rather than evidence
   * of absence. The row and its history are retained either way — the datasets that disappear are
   * the ones an auditor asks about.
   */
  readonly state: 'active' | 'vanished';
  /**
   * 0 until first observed, distinguishing "registered, never harvested" from "harvested and found
   * to have no columns".
   */
  readonly current_version: number;
}

export function isDataset(value: unknown): value is Dataset {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['connection_id'] === 'string' &&
    typeof candidate['schema_name'] === 'string' &&
    typeof candidate['object_name'] === 'string' &&
    (['active', 'vanished'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['current_version'] === 'number'
  );
}

/**
 * One row of the catalog browser: a dataset, plus how far its PII review has got. **A separate
 * schema from `Dataset`, not `Dataset` with optional extras.** The detail endpoint returns the
 * aggregate; this returns a read projection with a classification joined in. Modelled as one
 * schema with conditionally-present fields, every consumer would have to narrow `pii_columns`
 * before reading it, and a consumer that forgot would render an unexamined dataset as an examined,
 * clean one. So the four browse fields are required here and absent there. The shared fields are
 * repeated rather than composed because the TypeScript generator implements no `allOf`. That
 * duplication is the cost of the choice and is worth naming: a change to a shared field has to be
 * made in both schemas.
 */
export interface CatalogRow {
  /** Stable for the life of the dataset. Key client-side state on this. */
  readonly id: string;
  readonly connection_id: string;
  readonly schema_name: string;
  readonly object_name: string;
  /** Null means renames on this source cannot be tracked. */
  readonly source_object_id?: string | null;
  readonly state: 'active' | 'vanished';
  readonly current_version: number;
  /**
   * Whether any classification exists. **Read this before `pii_columns`.** `pii_columns: 0` with
   * `classified: false` means nobody has examined the dataset; with `classified: true` it means
   * the classifier examined it and flagged nothing. A client rendering the count alone shows those
   * two identically, and the first is not a clean bill of health.
   */
  readonly classified: boolean;
  /**
   * How far the PII review has got, or null when no classification exists. `inconclusive` is the
   * one to render distinctly: the classifier ran and could not decide, which is an absence of
   * evidence rather than a verdict. Showing it in the same visual language as `approved` is how a
   * column of Aadhaar numbers ends up on a dashboard someone trusts.
   */
  readonly classification: string | null;
  /** Columns flagged as personal data. Meaningless unless `classified` is true. */
  readonly pii_columns: number;
  /**
   * Server-computed: true for `stale` and `inconclusive` only. Not `pending` — something is
   * already working on it, and a queue including in-flight items is one people learn to skim. Not
   * `proposed` either: that belongs in the review queue, which has its own accept/reject
   * affordances, and two places to act with no way to tell which is current is worse than one.
   * Computed server-side so every surface agrees on what needs a steward.
   */
  readonly needs_attention: boolean;
}

export function isCatalogRow(value: unknown): value is CatalogRow {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['connection_id'] === 'string' &&
    typeof candidate['schema_name'] === 'string' &&
    typeof candidate['object_name'] === 'string' &&
    (['active', 'vanished'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['current_version'] === 'number' &&
    typeof candidate['classified'] === 'boolean' &&
    (['pending', 'proposed', 'approved', 'stale', 'inconclusive', null] as readonly unknown[]).includes(candidate['classification']) &&
    typeof candidate['pii_columns'] === 'number' &&
    typeof candidate['needs_attention'] === 'boolean'
  );
}

export interface DatasetVersion {
  readonly id: string;
  readonly dataset_id: string;
  readonly version_number: number;
  /**
   * SHA-256 over every observed column property including ordinal and comment. Decides whether a
   * new version is created.
   */
  readonly schema_checksum: string;
  /**
   * SHA-256 over only what a PII verdict depends on — name, type, nullability — sorted by
   * case-folded name and excluding ordinal. Decides whether an existing verdict is still valid.
   * Two checksums rather than one, so a comment edit produces a version without invalidating a
   * human-approved classification.
   */
  readonly classification_checksum: string;
  readonly columns: readonly ColumnDefinition[];
  readonly observed_at: string;
  /** Which harvest observed it — the provenance behind "why did this change appear at 03:00". */
  readonly harvest_id?: string | null;
}

export function isDatasetVersion(value: unknown): value is DatasetVersion {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['version_number'] === 'number' &&
    typeof candidate['schema_checksum'] === 'string' &&
    typeof candidate['classification_checksum'] === 'string' &&
    Array.isArray(candidate['columns']) &&
    (candidate['columns'] as readonly unknown[]).every((item) => isColumnDefinition(item)) &&
    typeof candidate['observed_at'] === 'string'
  );
}

export interface ColumnVerdict {
  readonly column_name: string;
  readonly kind: PiiKind;
  readonly confidence: number;
  /**
   * `deterministic` for a checksum validator (PAN, Aadhaar Verhoeff, GSTIN mod-36, IFSC, Luhn).
   * Anything else is advisory and must be validator-gated before it is trusted.
   */
  readonly method: string;
}

export function isColumnVerdict(value: unknown): value is ColumnVerdict {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['column_name'] === 'string' &&
    (['none', 'pan', 'aadhaar', 'gstin', 'ifsc', 'card_number', 'email', 'phone', 'name', 'address', 'other'] as readonly unknown[]).includes(candidate['kind']) &&
    typeof candidate['confidence'] === 'number' &&
    typeof candidate['method'] === 'string'
  );
}

/**
 * There is deliberately no `has_pii` boolean. A boolean would collapse "examined and found
 * nothing" with "could not tell", and a client rendering `inconclusive` as clean is exactly the
 * outcome that puts a column of Aadhaar numbers on a dashboard.
 */
export interface Classification {
  readonly dataset_id: string;
  readonly state: ClassificationState;
  /**
   * The classification checksum this verdict was computed against. Compare it with the dataset's
   * current version to see staleness for yourself.
   */
  readonly classified_against: string;
  readonly verdicts: readonly ColumnVerdict[];
  /** Why, for `inconclusive` and `stale`. The only actionable part of a failure. */
  readonly detail?: string | null;
  /**
   * Retained on a `stale` record too — who approved the superseded verdict is still the useful
   * fact for whoever reviews it next.
   */
  readonly approved_by?: string | null;
}

export function isClassification(value: unknown): value is Classification {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    (['pending', 'proposed', 'approved', 'stale', 'inconclusive'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['classified_against'] === 'string' &&
    Array.isArray(candidate['verdicts']) &&
    (candidate['verdicts'] as readonly unknown[]).every((item) => isColumnVerdict(item))
  );
}

export interface ObjectFailure {
  readonly qualified_name: string;
  /**
   * Per object rather than a count. "42 objects failed" is not actionable; "these 42 failed with
   * ORA-00942: table or view does not exist" says the harvest role is missing a grant.
   */
  readonly reason: string;
}

export function isObjectFailure(value: unknown): value is ObjectFailure {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['qualified_name'] === 'string' &&
    typeof candidate['reason'] === 'string'
  );
}

export interface HarvestProgress {
  /**
   * 0 until listing completes. A percentage before then would need an invented denominator, and a
   * made-up progress bar is worse than an honest count.
   */
  readonly discovered: number;
  readonly observed: number;
  readonly failed: number;
}

export function isHarvestProgress(value: unknown): value is HarvestProgress {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['discovered'] === 'number' &&
    typeof candidate['observed'] === 'number' &&
    typeof candidate['failed'] === 'number'
  );
}

/**
 * Every field is required. `_harvest_json` emits all of them on every path, and `absence_withheld`
 * defaults to 0 rather than being omitted *so that* a client never has to tell "nothing withheld"
 * apart from "this endpoint does not report it". Marking it optional here would reintroduce in the
 * generated client exactly the distinction that default removes.
 */
export interface HarvestOperation {
  readonly id: string;
  readonly connection_id: string;
  readonly state: HarvestState;
  readonly progress: HarvestProgress;
  readonly failures: readonly ObjectFailure[];
  /**
   * How many datasets this harvest declined to mark vanished because it did not observe the whole
   * source. 0 for a fully successful harvest. Non-zero means "the catalog may be stale by this
   * many datasets" — the fact an operator needs to decide whether to re-run.
   */
  readonly absence_withheld: number;
  readonly detail: string | null;
  readonly started_at: string | null;
  readonly finished_at: string | null;
}

export function isHarvestOperation(value: unknown): value is HarvestOperation {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['connection_id'] === 'string' &&
    (['pending', 'running', 'succeeded', 'partial', 'failed', 'cancelled'] as readonly unknown[]).includes(candidate['state']) &&
    candidate['progress'] !== undefined &&
    Array.isArray(candidate['failures']) &&
    (candidate['failures'] as readonly unknown[]).every((item) => isObjectFailure(item)) &&
    typeof candidate['absence_withheld'] === 'number' &&
    (typeof candidate['detail'] === 'string' || candidate['detail'] === null) &&
    (typeof candidate['started_at'] === 'string' || candidate['started_at'] === null) &&
    (typeof candidate['finished_at'] === 'string' || candidate['finished_at'] === null)
  );
}

/**
 * One retained value of a column's distribution, **identified but never disclosed**. The digest is
 * a **salted** MD5 of the value, computed by the source in its SELECT list. **There is no preview,
 * and its absence is the security fix rather than an omission.** This schema declared a `preview`
 * — the value's first two and last two characters — for longer than the API returned one. That
 * rendering is rung 4 of ADR-004's ladder verbatim ("bounded masked sample, masked at source"),
 * whose approval column is *tenant policy*, and it was being served on a rung-3 surface under
 * plain `read`, which `READER` blanket-grants on every resource type. A reader who by construction
 * cannot see a customer row could have recovered real identifiers. The field was removed from the
 * response; this is the contract catching up. The digest is salted for the same reason.
 * `md5(pan_no)` is one of ~1.8e7 candidates and an Aadhaar digest one of 1e12 with a check digit
 * to prune it — both brute-forceable offline by anyone holding the stored sketch.
 * `LUMIQ_SKETCH_SALT` makes a digest an identity rather than a recoverable value. The digest is
 * what makes drift comparable: it is stable across runs and across dialects, so "the value that
 * was 60% of this column last week is now 3%" is answerable without the platform ever holding the
 * value.
 */
export interface FrequentValue {
  readonly digest: string;
  /** Occurrences. Exact for a single scan; an upper bound after a merge. */
  readonly count: number;
  /**
   * How much of `count` may be overcount — the true count is in `[count - error, count]`. Zero for
   * a summary read straight from a source. Non-zero only after a merge, where a value absent from
   * one side could have sat just below that side's retention floor.
   */
  readonly error: number;
  readonly share: number;
}

export function isFrequentValue(value: unknown): value is FrequentValue {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['digest'] === 'string' &&
    typeof candidate['count'] === 'number' &&
    typeof candidate['error'] === 'number' &&
    typeof candidate['share'] === 'number'
  );
}

export interface ColumnSketch {
  readonly column: string;
  /**
   * Distinct non-null values. The source's exact count when `distinct_exact` is non-null,
   * otherwise a theta-sketch estimate with a relative standard error of about 3%.
   */
  readonly distinct_estimate: number;
  /**
   * The source's own exact distinct count, which its single grouping produces for free. **Null
   * after a merge**, because two exact distinct counts do not add — the same value may appear in
   * both partitions — so a merged sketch falls back to the estimator. Reported beside the estimate
   * rather than replacing it, so a reader can tell which they have.
   */
  readonly distinct_exact: number | null;
  /**
   * Whether both sketches enumerated everything. Reported rather than inferred, because the same
   * movement is a finding when measured and noise when it sits inside the error bar.
   */
  readonly exact: boolean;
  readonly null_count: number;
  readonly rows_scanned: number;
  /**
   * Non-null share. **Null for an empty relation, never 1.0** — an empty table is not fully
   * complete, and 1.0 would put a perfect score on a table with nothing in it. A column that is
   * entirely null is the different case and reports a measured 0.0.
   */
  readonly completeness: number | null;
  /**
   * The share held by the most frequent value, or null when nothing was retained. The single most
   * useful number here: a column that is 98% one value is a column whose rules will pass for a
   * reason nobody intended.
   */
  readonly dominant_share: number | null;
  readonly frequent_values: readonly FrequentValue[];
  readonly frequent_values_truncated: boolean;
}

export function isColumnSketch(value: unknown): value is ColumnSketch {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['column'] === 'string' &&
    typeof candidate['distinct_estimate'] === 'number' &&
    (typeof candidate['distinct_exact'] === 'number' || candidate['distinct_exact'] === null) &&
    typeof candidate['exact'] === 'boolean' &&
    typeof candidate['null_count'] === 'number' &&
    typeof candidate['rows_scanned'] === 'number' &&
    (typeof candidate['completeness'] === 'number' || candidate['completeness'] === null) &&
    (typeof candidate['dominant_share'] === 'number' || candidate['dominant_share'] === null) &&
    Array.isArray(candidate['frequent_values']) &&
    (candidate['frequent_values'] as readonly unknown[]).every((item) => isFrequentValue(item)) &&
    typeof candidate['frequent_values_truncated'] === 'boolean'
  );
}

export interface BaselineFinding {
  readonly column: string;
  /**
   * The reason, not a sentence — the wording belongs to the client's copy catalogue so the figure
   * is translatable. `all_null` is a measured 0.0 completeness, which is a different fact from a
   * column that could not be measured at all; `constant` is one distinct value and `dominated` is
   * almost one, which are separate findings because a reader needs to tell "this column has a
   * single value" from "this column is nearly useless".
   */
  readonly kind: 'all_null' | 'mostly_null' | 'sparse' | 'constant' | 'dominated';
  /**
   * Points subtracted. Never zero — a row reading "sparse, -0" trains a reader to skip the list
   * the figure depends on.
   */
  readonly deduction: number;
  /** The measured figure the deduction came from, so a row shows its own evidence. */
  readonly observed: number;
}

export function isBaselineFinding(value: unknown): value is BaselineFinding {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['column'] === 'string' &&
    (['all_null', 'mostly_null', 'sparse', 'constant', 'dominated'] as readonly unknown[]).includes(candidate['kind']) &&
    typeof candidate['deduction'] === 'number' &&
    typeof candidate['observed'] === 'number'
  );
}

/**
 * What this profile alone says, and every deduction the figure is made of. **Subtractive and
 * itemised rather than a blended average.** A uniqueness ratio of 0.3 on a low-cardinality
 * dimension column is not 30% quality, and a column that is 60% null is not a defect if it is
 * optional — if every component needs "unless this column is meant to be that way", the average is
 * not a quality score. So it starts at 100 and subtracts for findings, and the value always equals
 * 100 minus the deductions listed here. A score a steward cannot decompose is a score they ignore.
 * Uniqueness, timeliness and accuracy are deliberately absent: the first needs a declared key, the
 * second an expected cadence, the third a reference source. Each is a rule rather than a
 * statistic, and inventing a figure for any of them would be the platform faking a dimension.
 */
export interface ProfileBaseline {
  /**
   * Null when nothing could be measured, which is **not** a zero: a dataset whose every column
   * came back unmeasured has no baseline, and 100 would put a perfect score on an empty table
   * while 0 would call it the worst in the estate.
   */
  readonly value: number | null;
  /** Every deduction, worst first, with a stable tie-break so two reads agree. */
  readonly findings: readonly BaselineFinding[];
  /** Columns the profile could not measure. Reported rather than scored. */
  readonly unmeasured_columns: readonly string[];
  /** How many columns the figure rests on, so a one-column profile is not read as an estate. */
  readonly columns_measured: number;
}

export function isProfileBaseline(value: unknown): value is ProfileBaseline {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (typeof candidate['value'] === 'number' || candidate['value'] === null) &&
    Array.isArray(candidate['findings']) &&
    (candidate['findings'] as readonly unknown[]).every((item) => isBaselineFinding(item)) &&
    Array.isArray(candidate['unmeasured_columns']) &&
    (candidate['unmeasured_columns'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['columns_measured'] === 'number'
  );
}

export interface DatasetSketches {
  readonly dataset_id: string;
  readonly dataset_version_id: string;
  /**
   * The newest column's scan time. A wide table is profiled across several requests, so there is
   * no single instant for the set; the newest never claims a sketch is fresher than it is.
   */
  readonly computed_at: string;
  readonly columns: readonly ColumnSketch[];
  readonly baseline: ProfileBaseline;
}

export function isDatasetSketches(value: unknown): value is DatasetSketches {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['dataset_version_id'] === 'string' &&
    typeof candidate['computed_at'] === 'string' &&
    Array.isArray(candidate['columns']) &&
    (candidate['columns'] as readonly unknown[]).every((item) => isColumnSketch(item)) &&
    candidate['baseline'] !== undefined
  );
}

export interface ColumnDrift {
  readonly column: string;
  readonly distinct_before: number;
  readonly distinct_after: number;
  /**
   * Relative change in distinct count. **Null when the baseline was empty**, not zero: a column
   * that had no values and now has some has not changed by a percentage, and 0.0 would say nothing
   * happened.
   */
  readonly distinct_change: number | null;
  /** Estimated overlap of the two value sets. The figure a stored count cannot produce. */
  readonly jaccard: number;
  /** The share of the smaller value set found in the larger. */
  readonly containment: number;
  /**
   * True when more of the two value sets differ than agree — the finding a count cannot produce, a
   * population that turned over without changing size. **Null when neither version had any
   * values**, and that is not a subtlety: Jaccard returns 0.0 for an empty union, so two all-null
   * columns — or an empty staging table profiled twice — would read as a total population
   * turnover, on every column. A client must render null as "cannot say" rather than as `false`,
   * which claims stability nobody measured, and certainly not as `true`. Every other nullable
   * field in this schema explains its own null; this one did not, and hard rule 10 makes this
   * document the place a client author learns it.
   */
  readonly values_were_replaced: boolean | null;
  readonly completeness_before: number | null;
  readonly completeness_after: number | null;
  readonly dominant_share_before: number | null;
  readonly dominant_share_after: number | null;
  /** True only when all four sketches involved were exact. */
  readonly exact: boolean;
}

export function isColumnDrift(value: unknown): value is ColumnDrift {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['column'] === 'string' &&
    typeof candidate['distinct_before'] === 'number' &&
    typeof candidate['distinct_after'] === 'number' &&
    (typeof candidate['distinct_change'] === 'number' || candidate['distinct_change'] === null) &&
    typeof candidate['jaccard'] === 'number' &&
    typeof candidate['containment'] === 'number' &&
    (typeof candidate['values_were_replaced'] === 'boolean' || candidate['values_were_replaced'] === null) &&
    (typeof candidate['completeness_before'] === 'number' || candidate['completeness_before'] === null) &&
    (typeof candidate['completeness_after'] === 'number' || candidate['completeness_after'] === null) &&
    (typeof candidate['dominant_share_before'] === 'number' || candidate['dominant_share_before'] === null) &&
    (typeof candidate['dominant_share_after'] === 'number' || candidate['dominant_share_after'] === null) &&
    typeof candidate['exact'] === 'boolean'
  );
}

export interface SketchDrift {
  readonly dataset_id: string;
  readonly baseline_version_id: string;
  readonly current_version_id: string;
  /**
   * How the report was produced. Stated on the response rather than left to be inferred, because
   * it is the property that makes this endpoint cheap and it is not obvious from a payload of
   * numbers.
   */
  readonly computed_from: string;
  readonly columns: readonly ColumnDrift[];
  /**
   * Columns only the current side had. Reported rather than skipped: an appearance is either a
   * schema change or a profiling bound somebody widened, and both are worth seeing.
   */
  readonly added_columns: readonly string[];
  readonly removed_columns: readonly string[];
}

export function isSketchDrift(value: unknown): value is SketchDrift {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['baseline_version_id'] === 'string' &&
    typeof candidate['current_version_id'] === 'string' &&
    typeof candidate['computed_from'] === 'string' &&
    Array.isArray(candidate['columns']) &&
    (candidate['columns'] as readonly unknown[]).every((item) => isColumnDrift(item)) &&
    Array.isArray(candidate['added_columns']) &&
    (candidate['added_columns'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    Array.isArray(candidate['removed_columns']) &&
    (candidate['removed_columns'] as readonly unknown[]).every((item) => typeof item === 'string')
  );
}

/**
 * One source family as this deployment can actually use it. Nothing here is tenant data and
 * nothing here is a secret value.
 */
export interface ConnectorProfile {
  readonly connector_type: 'postgresql' | 'mysql' | 'oracle' | 'sqlserver' | 'snowflake' | 'databricks' | 'bigquery';
  /**
   * A metadata connector is wired, so datasets can be created from this source. False means
   * registration is refused with a 422 rather than accepted and left to fail at first harvest.
   */
  readonly harvestable: boolean;
  /**
   * A pushdown driver exists, so a rule can run against this source. False on a harvestable family
   * means the catalogue is readable and no rule can be bound.
   */
  readonly executes_rules: boolean;
  /**
   * Column sketches and drift comparison are available. Also false when the deployment has no
   * sketch salt, which is a configuration answer rather than a connector one.
   */
  readonly profiles_columns: boolean;
  /**
   * Credential field names this provider must be given. Empty for a family with no adapter.
   * Snowflake's password-or-private-key choice is deliberately **not** expressed here: this is the
   * unconditionally required set, and a flat list cannot say "one of these two". The mechanism is
   * a constructor refusal, and a form asks it as a toggle.
   */
  readonly required_fields: readonly string[];
  /** Credential field names this provider accepts and does not demand. */
  readonly optional_fields: readonly string[];
  /**
   * Which of the required and optional field **names** hold a secret. Field names only; no value
   * is ever returned by any operation in this document. Intersected with this provider's own
   * fields, so a client does not learn that some other provider has a token.
   */
  readonly secret_fields: readonly string[];
  /**
   * Whether host, port and database are part of addressing this source. False for the HTTPS
   * statement APIs, where the account identifier or workspace host is inseparable from
   * authenticating and therefore travels in the credential.
   */
  readonly needs_endpoint: boolean;
}

export function isConnectorProfile(value: unknown): value is ConnectorProfile {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['postgresql', 'mysql', 'oracle', 'sqlserver', 'snowflake', 'databricks', 'bigquery'] as readonly unknown[]).includes(candidate['connector_type']) &&
    typeof candidate['harvestable'] === 'boolean' &&
    typeof candidate['executes_rules'] === 'boolean' &&
    typeof candidate['profiles_columns'] === 'boolean' &&
    Array.isArray(candidate['required_fields']) &&
    (candidate['required_fields'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    Array.isArray(candidate['optional_fields']) &&
    (candidate['optional_fields'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    Array.isArray(candidate['secret_fields']) &&
    (candidate['secret_fields'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['needs_endpoint'] === 'boolean'
  );
}

/**
 * One schema and how many objects a harvest would find in it. A schema with no objects is absent
 * rather than present with a zero.
 */
export interface SchemaPreview {
  /**
   * The schema as the *source* reports it, not normalised — an operator recognises `FINPROD`, and
   * the normalised form would show them `finprod` on an Oracle source, which is a name they have
   * never seen. A source with no schema concept groups under a host-written label.
   */
  readonly name: string;
  /** Tables and views, not rows. */
  readonly objects: number;
}

export function isSchemaPreview(value: unknown): value is SchemaPreview {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['name'] === 'string' &&
    typeof candidate['objects'] === 'number'
  );
}

export interface SchemaPreviewPage {
  readonly items: readonly SchemaPreview[];
  readonly meta: Readonly<Record<string, unknown>>;
}

export function isSchemaPreviewPage(value: unknown): value is SchemaPreviewPage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isSchemaPreview(item)) &&
    candidate['meta'] !== undefined
  );
}

/**
 * One source, as much as the catalogue can say without opening it. **Every nullable field here
 * means something a zero would misstate**, and that is the whole contract of this schema. Read
 * each null as its own sentence rather than as a missing number.
 */
export interface ConnectionSummary {
  readonly connection_id: string;
  /**
   * Undeleted datasets harvested through this source, in whatever state. Includes `vanished` ones,
   * so this figure agrees with the listing on the next screen.
   */
  readonly datasets: number;
  /**
   * Distinct datasets carrying at least one score. **`null` means scoring was not consulted** —
   * the deployment has not wired the counter, or the read failed — and is not the same as `0`,
   * which means it was asked and found none. A client rendering null as zero reports an estate
   * nobody has ever scored.
   */
  readonly scored: number | null;
  /**
   * When a harvest of this source last *succeeded*, or `null` for never. Successful runs only: a
   * failure an hour ago does not make the catalogue an hour old, and dating it to a run that read
   * nothing would be worse than saying nothing.
   */
  readonly last_harvest_at: string | null;
  /**
   * The state of the most recent harvest whether it succeeded or not, or `null` when none has ever
   * been requested. Paired with `last_harvest_at` because the two answer different questions —
   * "how old is this catalogue" against "is something wrong right now" — and a single field
   * cannot.
   */
  readonly harvest_state: string | null;
  /** Objects the most recent harvest expected to read. Zero until a run reports it. */
  readonly discovered: number;
  /**
   * Objects it has read so far. With `discovered`, what makes a running harvest legible as `312 of
   * 504` rather than as an unbounded spinner.
   */
  readonly observed: number;
}

export function isConnectionSummary(value: unknown): value is ConnectionSummary {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['connection_id'] === 'string' &&
    typeof candidate['datasets'] === 'number' &&
    (typeof candidate['scored'] === 'number' || candidate['scored'] === null) &&
    (typeof candidate['last_harvest_at'] === 'string' || candidate['last_harvest_at'] === null) &&
    (typeof candidate['harvest_state'] === 'string' || candidate['harvest_state'] === null) &&
    typeof candidate['discovered'] === 'number' &&
    typeof candidate['observed'] === 'number'
  );
}

/**
 * The two things a dataset listing needs that live in the profile rather than the catalogue. Both
 * are null together only for a dataset that was profiled and reported nothing — a dataset never
 * profiled is **absent from the page** instead.
 */
export interface DatasetFacts {
  readonly dataset_id: string;
  /**
   * Rows the last profile scanned. **Not the table's current row count**, which this platform does
   * not hold: a harvest reads `information_schema` and never counts rows, so the only figure that
   * exists is what a profile actually scanned. Render it with `last_profiled_at` beside it, or a
   * figure from three weeks ago reads as current.
   */
  readonly rows_at_last_profile: number | null;
  /** When that scan ran. What makes the figure above interpretable at all. */
  readonly last_profiled_at: string | null;
  /**
   * What the last profile alone says about this dataset, 0-100. **Provisional, and never the rule
   * score**: it is derived from statistics with no human input, so where a rule score exists that
   * one is authoritative and this is context about which rules to write next. A screen showing
   * both must mark which is which. Null both when the dataset was never profiled and when it was
   * profiled and every column came back unmeasured, which is what an empty relation produces.
   * Neither is a zero, and `last_profiled_at` is what separates them — read the pair, never this
   * alone.
   */
  readonly profile_baseline: number | null;
}

export function isDatasetFacts(value: unknown): value is DatasetFacts {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    (typeof candidate['rows_at_last_profile'] === 'number' || candidate['rows_at_last_profile'] === null) &&
    (typeof candidate['last_profiled_at'] === 'string' || candidate['last_profiled_at'] === null) &&
    (typeof candidate['profile_baseline'] === 'number' || candidate['profile_baseline'] === null)
  );
}

export interface HarvestScheduleWrite {
  /**
   * Omitted means false. A schedule can be stored disabled and switched on later, which is how a
   * cadence is agreed before anybody starts scanning.
   */
  readonly enabled?: boolean;
  /**
   * `daily` covers all seven days; `weekly` covers exactly the days named. There is deliberately
   * no hourly member — a harvest enumerates a production database's whole catalog — and no cron
   * expression, because a cron string carries its own timezone semantics and cannot be said back
   * to a human as the sentence they chose.
   */
  readonly cadence: 'daily' | 'weekly';
  /**
   * Wall-clock time in `timezone`, 24-hour `HH:MM`. Seconds and offset suffixes are refused: the
   * zone is a separate field, deliberately, so there is exactly one place the timezone question is
   * answered.
   */
  readonly local_time: string;
  /**
   * IANA zone name. **Never an offset.** `UTC+05:30` is correct for India for ever and wrong for
   * every DST market, silently, for six months at a time — so it is refused by the domain and by a
   * CHECK constraint rather than accepted and misinterpreted.
   */
  readonly timezone: string;
  /**
   * Monday 0 through Sunday 6, matching Python's `datetime.weekday()` and `ExecutionWindow`.
   * Evaluated against the **local** date: a 01:00 Asia/Kolkata schedule on a Monday is 19:30 UTC
   * on the Sunday, so reading the weekday off the instant would run it on the wrong day every
   * week. Omitted means all seven, which `daily` requires.
   */
  readonly weekdays?: readonly number[];
}

export function isHarvestScheduleWrite(value: unknown): value is HarvestScheduleWrite {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['daily', 'weekly'] as readonly unknown[]).includes(candidate['cadence']) &&
    typeof candidate['local_time'] === 'string' &&
    typeof candidate['timezone'] === 'string'
  );
}

export interface HarvestSchedule {
  readonly connection_id: string;
  /**
   * False when nobody has set a schedule for this source, in which case every field below is
   * absent and `detail` says so. Distinct from `enabled: false`, which is a decision somebody took
   * and can undo.
   */
  readonly configured: boolean;
  readonly enabled: boolean;
  /** Present only when `configured` is false. */
  readonly detail?: string;
  readonly cadence?: 'daily' | 'weekly';
  readonly local_time?: string;
  /**
   * Always returned beside `local_time`. `02:00` alone is the ambiguity this whole feature exists
   * to remove, so a client is never left to remember the zone.
   */
  readonly timezone?: string;
  readonly weekdays?: readonly number[];
  /**
   * The schedule as a sentence, built by the platform. The parts above are what a localised UI
   * needs in order to build its own wording; this is the fallback and the thing a log line or an
   * audit entry can carry.
   */
  readonly describes?: string;
  /**
   * The next occurrence as a UTC instant, or null. Null for a **disabled** schedule — a future run
   * shown for something that will not happen is worse than showing nothing — and null for an
   * enabled one that a sweep has not yet primed. This is a *cache*, derived from `local_time` and
   * `timezone` on every claim. The pair is the configuration; if the two ever disagree, the pair
   * wins.
   */
  readonly next_run_at?: string | null;
  readonly last_run_at?: string | null;
  /**
   * Null when no occurrence has been reached — absence is not an outcome. `requested` means a
   * harvest was dispatched, **not** that it succeeded: the harvest row owns that, and reporting a
   * success here would be this record claiming an outcome it never observed. `deferred` means the
   * execution window was shut or the source was at its concurrency bound, which is the
   * configuration working at an inconvenient moment. `refused` means the platform declined and
   * somebody has to act. `failed` means the dispatch broke inside Lumiq, which pages us rather
   * than the customer. `skipped` means the schedule or the source was switched off between the
   * claim and the dispatch.
   */
  readonly last_outcome?: string | null;
  /**
   * One host-written sentence about `last_outcome`. Never a driver message: a driver error carries
   * the failing statement and with it the tenant's own literals.
   */
  readonly last_detail?: string | null;
  /**
   * True only for `refused` and `failed`. A deferral deliberately does not ask for attention — a
   * screen that highlighted every deferral would train somebody to ignore the highlight.
   */
  readonly needs_attention?: boolean;
  /**
   * True when the schedule has paused itself after consecutive refusals. Distinct from disabled: a
   * disabled schedule is somebody's decision, a suspension is a condition and it lapses on its
   * own.
   */
  readonly suspended?: boolean;
  readonly suspended_until?: string | null;
  /**
   * Why it paused, and what to fix. Travels with `suspended_until` — a suspension with no reason
   * cannot be cleared by anybody who did not cause it.
   */
  readonly suspension_reason?: string | null;
  readonly configured_by?: string;
  readonly configured_at?: string;
}

export function isHarvestSchedule(value: unknown): value is HarvestSchedule {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['connection_id'] === 'string' &&
    typeof candidate['configured'] === 'boolean' &&
    typeof candidate['enabled'] === 'boolean'
  );
}

export interface AutoProfileQueueCounts {
  /** Queued and never yet attempted. */
  readonly pending: number;
  /**
   * Queued again after a failed attempt, waiting out a backoff. **Separate from `failed` on
   * purpose**: a queue healing itself and one that has given up need opposite responses from a
   * reader, and one figure would hide which they are looking at.
   */
  readonly retrying: number;
  /** Dispatched, with the scan in progress. */
  readonly running: number;
  readonly succeeded: number;
  /**
   * Attempts exhausted. The only figure here that means somebody should look - the others resolve
   * by themselves.
   */
  readonly failed: number;
  /**
   * `pending + retrying + running`. Derived by the server so every surface that renders "still to
   * do" agrees; two clients adding three figures is two places it can drift.
   */
  readonly outstanding: number;
}

export function isAutoProfileQueueCounts(value: unknown): value is AutoProfileQueueCounts {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['pending'] === 'number' &&
    typeof candidate['retrying'] === 'number' &&
    typeof candidate['running'] === 'number' &&
    typeof candidate['succeeded'] === 'number' &&
    typeof candidate['failed'] === 'number' &&
    typeof candidate['outstanding'] === 'number'
  );
}

export interface AutoProfile {
  readonly connection_id: string;
  /**
   * False when nobody has chosen for this source, in which case `enabled` is the platform default
   * of false and `configured_by` is null. Distinct from `enabled: false`, which is a decision
   * somebody took - reporting a default as a decision is what this flag exists to prevent.
   */
  readonly configured: boolean;
  /**
   * Whether a harvest of this source queues its new and changed datasets for profiling. Never its
   * unchanged ones: a nightly harvest of a stable source queues nothing.
   */
  readonly enabled: boolean;
  /**
   * Who last set this, and therefore who held `catalog.dataset:profile` when these scans were
   * authorized. Null when `configured` is false.
   */
  readonly configured_by?: string | null;
  /** UTC instant with an offset. Null when `configured` is false. */
  readonly configured_at?: string | null;
  readonly queue: AutoProfileQueueCounts;
}

export function isAutoProfile(value: unknown): value is AutoProfile {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['connection_id'] === 'string' &&
    typeof candidate['configured'] === 'boolean' &&
    typeof candidate['enabled'] === 'boolean' &&
    candidate['queue'] !== undefined
  );
}
